/* Your looks page (theme-changes §66).
 *
 * State is the customer's document: a list of named looks, each with its
 * colours. The [+] block asks for a name and becomes a look block; tapping
 * a look opens it (the rest dim) and the palette below edits it. Every
 * change posts the WHOLE document to the app proxy, so there is nothing
 * to "save" — tapping another look or the page body just closes the
 * editor. On failure the document reverts and the status line says so.
 * Logged out there is no post: the page works as a taste-test. */
(() => {
  const root = document.querySelector('.yc[data-your-colours-url]');
  if (!root) return;
  const url = root.dataset.yourColoursUrl;
  const loggedIn = !!root.dataset.customer;
  const looksEl = root.querySelector('[data-looks]');
  const editor = root.querySelector('[data-editor]');
  const removeBtn = root.querySelector('[data-remove-look]');
  const doneBtn = root.querySelector('[data-done]');
  const status = root.querySelector('[data-status]');
  const chips = Array.from(root.querySelectorAll('.yc-chip'));
  const MAX_LOOKS = 8;
  const MAX_COLOURS = 24;

  let looks = [];
  try {
    const doc = JSON.parse(root.dataset.yourColours || 'null');
    looks = Array.isArray(doc) ? [{ id: 'look_1', name: 'Your colours', colours: doc }] : (doc && doc.looks) || [];
  } catch { looks = []; }
  looks = looks.filter((l) => l && l.id && l.name).map((l) => ({ ...l, colours: (l.colours || []).filter((c) => c && c.key) }));

  let activeId = null;
  let naming = false;
  // The blur that completes a naming and the click that caused it are one
  // gesture; the click must not then land on the body and close the
  // palette the blur just opened.
  let swallowNextBodyClick = false;
  const blocks = new Map(); // id -> element (kept across renders so transitions run)
  const idOf = (c) => `${c.material || 'rubber'}:${c.key}`;
  const active = () => looks.find((l) => l.id === activeId) || null;

  function say(msg) {
    if (!status) return;
    status.textContent = msg;
    clearTimeout(say.t);
    say.t = setTimeout(() => { status.textContent = ''; }, 2500);
  }

  /* ---- look blocks ---- */
  function makeBlock(look) {
    const el = document.createElement('div');
    el.className = 'yc-look';
    el.dataset.id = look.id;
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    const band = document.createElement('span');
    band.className = 'yc-look__band';
    const name = document.createElement('input');
    name.className = 'yc-look__name';
    name.type = 'text';
    name.maxLength = 40;
    name.readOnly = true;
    name.tabIndex = -1;
    name.setAttribute('aria-label', 'Palette name');
    el.append(band, name);
    el.addEventListener('click', (e) => {
      if (e.target === name && !name.readOnly) return;
      if (activeId === look.id) { stepBase(look.id); return; }
      open(look.id);
    });
    el.addEventListener('keydown', (e) => {
      if (e.target === el && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(look.id); }
    });
    name.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === 'Escape') name.blur(); });
    name.addEventListener('blur', () => {
      const l = looks.find((x) => x.id === look.id);
      const v = name.value.trim();
      if (!l || !v || v === l.name) { name.value = l ? l.name : ''; return; }
      commit(looks.map((x) => (x.id === l.id ? { ...x, name: v } : x)));
    });
    return el;
  }

  function paintBand(band, colours) {
    const have = new Map(Array.from(band.children).map((s) => [s.dataset.id, s]));
    const want = new Set(colours.map(idOf));
    for (const [id, s] of have) {
      if (want.has(id) || s.classList.contains('is-out')) continue;
      s.classList.add('is-out');
      s.addEventListener('transitionend', () => s.remove(), { once: true });
    }
    // The first colour is the base and fills two thirds of the block; the
    // rest share the remaining third as equal stripes down the edge (a
    // swatch, not a flag). One colour = solid.
    const n = colours.length;
    let prev = null;
    colours.forEach((c, i) => {
      const id = idOf(c);
      let s = have.get(id);
      if (!s || s.classList.contains('is-out')) {
        if (s) s.remove();
        s = document.createElement('span');
        s.className = 'yc-look__stripe is-out';
        s.dataset.id = id;
        s.style.backgroundColor = c.swatch;
        requestAnimationFrame(() => requestAnimationFrame(() => s.classList.remove('is-out')));
      }
      s.style.setProperty('--grow', i === 0 ? String(Math.max(1, 2 * (n - 1))) : '1');
      if (prev) prev.after(s); else band.prepend(s);
      prev = s;
    });
  }

  // Examples for the empty state: two stepped-back blocks that say what a
  // look is (a name, a base, a few others) without being tappable or
  // naming anything for the customer. Gone once a first look exists.
  const EXAMPLES = [
    { name: 'Classic', colours: ['#000000', '#f20000', '#eeeeee'] },
    { name: 'Night', colours: ['#000000', '#293ad7'] },
  ];
  let examples = null;
  function makeExamples() {
    return EXAMPLES.map((ex) => {
      const el = document.createElement('div');
      el.className = 'yc-look yc-look--example';
      el.setAttribute('aria-hidden', 'true');
      const band = document.createElement('span');
      band.className = 'yc-look__band';
      const n = ex.colours.length;
      ex.colours.forEach((hex, i) => {
        const st = document.createElement('span');
        st.className = 'yc-look__stripe';
        st.style.backgroundColor = hex;
        st.style.setProperty('--grow', i === 0 ? String(Math.max(1, 2 * (n - 1))) : '1');
        band.append(st);
      });
      const name = document.createElement('span');
      name.className = 'yc-look__name';
      name.textContent = ex.name;
      const cap = document.createElement('span');
      cap.className = 'yc-look__caption';
      cap.textContent = 'example';
      el.append(band, name, cap);
      return el;
    });
  }

  let newBlock = null;
  function makeNewBlock() {
    const el = document.createElement('div');
    el.className = 'yc-look yc-look--new';
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    const band = document.createElement('span');
    band.className = 'yc-look__band';
    band.textContent = '+';
    const name = document.createElement('input');
    name.className = 'yc-look__name';
    name.type = 'text';
    name.maxLength = 40;
    name.placeholder = 'Name this palette';
    name.value = 'New palette';
    name.readOnly = true;
    name.tabIndex = -1;
    name.setAttribute('aria-label', 'New palette name');
    el.append(band, name);
    el.addEventListener('click', (e) => {
      if (naming) return;
      naming = true;
      activeId = null;
      name.readOnly = false;
      name.tabIndex = 0;
      name.value = '';
      render();
      name.focus();
    });
    el.addEventListener('keydown', (e) => {
      if (e.target === el && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); el.click(); }
    });
    name.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); name.blur(); }
      if (e.key === 'Escape') { name.value = ''; name.blur(); }
    });
    name.addEventListener('blur', () => {
      const v = name.value.trim();
      naming = false;
      name.readOnly = true;
      name.tabIndex = -1;
      name.value = 'New palette';
      if (!v) { render(); return; }
      const id = `look_${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
      swallowNextBodyClick = true;
      setTimeout(() => { swallowNextBodyClick = false; }, 400);
      commit(looks.concat([{ id, name: v, colours: [] }]), () => { activeId = id; });
    });
    return el;
  }

  function render() {
    // look blocks, reconciled by id
    for (const [id, el] of blocks) if (!looks.some((l) => l.id === id)) { el.remove(); blocks.delete(id); }
    let prev = null;
    for (const l of looks) {
      let el = blocks.get(l.id);
      if (!el) { el = makeBlock(l); blocks.set(l.id, el); }
      const name = el.querySelector('.yc-look__name');
      if (document.activeElement !== name) name.value = l.name;
      const isActive = l.id === activeId;
      el.classList.toggle('is-active', isActive);
      el.title = isActive && l.colours.length > 1 ? 'Tap to change the base colour' : '';
      name.readOnly = !isActive;
      name.tabIndex = isActive ? 0 : -1;
      paintBand(el.querySelector('.yc-look__band'), l.colours);
      if (prev) prev.after(el); else looksEl.prepend(el);
      prev = el;
    }
    if (!examples) examples = makeExamples();
    for (const ex of examples) { if (looks.length) ex.remove(); else looksEl.append(ex); }
    if (!newBlock) newBlock = makeNewBlock();
    newBlock.classList.toggle('is-naming', naming);
    newBlock.hidden = looks.length >= MAX_LOOKS;
    looksEl.append(newBlock);
    looksEl.classList.toggle('is-empty', !looks.length);

    // editor
    const l = active();
    root.classList.toggle('is-editing', !!l);
    editor.hidden = !l;
    const saved = new Set(l ? l.colours.map(idOf) : []);
    for (const ch of chips) {
      const on = saved.has(`${ch.dataset.material || 'rubber'}:${ch.dataset.key}`);
      ch.classList.toggle('is-saved', on);
      ch.setAttribute('aria-pressed', String(on));
    }
    if (removeBtn) { removeBtn.classList.remove('is-armed'); removeBtn.textContent = 'Remove this palette'; }
  }

  // Step the base: the next colour becomes the base and the old base
  // joins the rest. Only the base matters, so a full reorder is not
  // needed — a few taps visit every colour.
  function stepBase(id) {
    const l = looks.find((x) => x.id === id);
    if (!l || l.colours.length < 2) return;
    const colours = l.colours.slice(1).concat([l.colours[0]]);
    commit(looks.map((x) => (x.id === id ? { ...x, colours } : x)));
  }

  function open(id) {
    if (activeId === id) return;
    activeId = id;
    naming = false;
    render();
  }
  function close() {
    if (activeId === null && !naming) return;
    activeId = null;
    // The editor is about to go, and with it most of the page's height.
    // Put the scroll on the palette row FIRST, or the browser clamps the
    // now-too-deep position to the bottom and lands on the footer.
    const top = looksEl.getBoundingClientRect().top + window.scrollY - 120;
    if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top) });
    render();
  }

  /* ---- persistence ----
   * Every change applies at once; saves are coalesced. One request runs
   * at a time, and taps made while it runs are folded into one more
   * request carrying the latest state when it finishes. Nothing ever
   * waits on the network. On failure the page reverts to the last
   * state the server confirmed. */
  let confirmed = looks;
  let inflight = false;
  let dirty = false;

  function commit(next, after) {
    looks = next;
    if (after) after();
    render();
    if (loggedIn) save();
  }

  async function save() {
    if (inflight) { dirty = true; return; }
    inflight = true;
    try {
      do {
        dirty = false;
        const snapshot = looks;
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ looks: snapshot }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !Array.isArray(data.looks)) throw new Error(data.error || String(res.status));
        confirmed = data.looks;
        // Adopt the server's copy only if nothing changed meanwhile;
        // otherwise the loop sends the newer state next.
        if (!dirty) { looks = data.looks; render(); }
      } while (dirty);
    } catch (err) {
      console.warn('[your-colours] save failed:', err);
      dirty = false;
      looks = confirmed;
      if (activeId && !looks.some((l) => l.id === activeId)) activeId = null;
      render();
      say('Could not save that — please try again.');
    } finally {
      inflight = false;
    }
  }

  function toggleColour(ch) {
    const l = active();
    if (!l) return;
    const key = ch.dataset.key;
    const material = ch.dataset.material || 'rubber';
    const i = l.colours.findIndex((c) => c.key === key && (c.material || 'rubber') === material);
    let colours;
    if (i >= 0) colours = l.colours.filter((_, n) => n !== i);
    else {
      if (l.colours.length >= MAX_COLOURS) { say(`That is ${MAX_COLOURS} colours — the most a look holds.`); return; }
      // First tapped is the base, the way a look is built: main colour
      // first, then the rest. Prepending (newest = base) was tried and
      // felt backwards. A tap on the open block steps the base instead.
      colours = l.colours.concat([{ key, label: ch.dataset.label, swatch: ch.dataset.swatch, material }]);
    }
    commit(looks.map((x) => (x.id === l.id ? { ...x, colours } : x)));
  }

  for (const ch of chips) ch.addEventListener('click', () => toggleColour(ch));

  // Remove: two taps, no dialog. The first arms it for three seconds.
  if (removeBtn) removeBtn.addEventListener('click', () => {
    const l = active();
    if (!l) return;
    if (!removeBtn.classList.contains('is-armed')) {
      removeBtn.classList.add('is-armed');
      removeBtn.textContent = `Tap again to remove "${l.name}"`;
      clearTimeout(removeBtn.t);
      removeBtn.t = setTimeout(() => { removeBtn.classList.remove('is-armed'); removeBtn.textContent = 'Remove this palette'; }, 3000);
      return;
    }
    commit(looks.filter((x) => x.id !== l.id), () => { activeId = null; });
  });

  // Done: the explicit way out, for anyone who does not find the body tap.
  if (doneBtn) doneBtn.addEventListener('click', () => close());

  // Body tap closes the editor. Anything inside a look block or the
  // editor is not "the body".
  document.addEventListener('click', (e) => {
    if (swallowNextBodyClick) { swallowNextBodyClick = false; return; }
    if (!root.classList.contains('is-editing') && !naming) return;
    if (e.target.closest('.yc-look') || e.target.closest('[data-editor]')) return;
    close();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && document.activeElement?.tagName !== 'INPUT') close(); });

  render();
})();
